import React, { useEffect, useState, useRef } from 'react';
import '.././App.css';
import Swal from 'sweetalert2';
import { auth, db } from '../firebase';
import {
  collection,
  addDoc,
  Timestamp,
  query,
  orderBy,
  onSnapshot,
  getDoc,
  getDocs,
  deleteDoc,
  doc,
  where,
  updateDoc,
  getAggregateFromServer,
  sum
} from 'firebase/firestore';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';

export default function CustomerAccount() {
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });

  const printRef = useRef();

  const [userData, setUserData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [payhistory, setPayhistory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [modalCustId, setModalCustId] = useState("");
  const [modalTotal, setModalTotal] = useState(0);
  const [modalPrevPaid, setModalPrevPaid] = useState(0);
  const [modalCurrentPaid, setModalCurrentPaid] = useState(0);
  const [modalRemaining, setModalRemaining] = useState(0);
  const [payType, setPayType] = useState('Cash');
  const [paidDate, setPaidDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });

  const [orderCustId, setOrderCustId] = useState("");
  const [orderAmount, setOrderAmount] = useState(0);
  const [orderDate, setOrderDate] = useState("");
  const [orderDescription, setOrderDescription] = useState("");

  const [loading, setLoading] = useState(false);

  const handlePrint = useReactToPrint({ content: () => printRef.current });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => setUserData(u));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchAllCustomersWithAggregates();
  }, []);

  const getSumForCustomer = async (collectionName, custid, fieldName) => {
    try {
      const q = query(collection(db, collectionName), where('custid', '==', custid));
      const agg = await getAggregateFromServer(q, { total: sum(fieldName) });
      return parseFloat(agg.data().total || 0);
    } catch (err) {
      console.warn('Aggregate failed for', collectionName, custid, err.message);
      try {
        const q2 = query(collection(db, collectionName), where('custid', '==', custid));
        const s = await getDocs(q2);
        let tot = 0;
        s.forEach(d => { tot += parseFloat(d.data()[fieldName] || 0); });
        return tot;
      } catch (e) {
        console.error(e);
        return 0;
      }
    }
  };

  const fetchAllCustomersWithAggregates = async () => {
    setLoading(true);
    try {
      const custQ = query(collection(db, 'customers'), orderBy('created', 'desc'));
      const custSnap = await getDocs(custQ);
      const custDocs = custSnap.docs;

      const customersResult = await Promise.all(custDocs.map(async (c) => {
        const id = c.id;
        const data = c.data();

        // Total sum from orderitems
        const totalOrders = await getSumForCustomer('orderitems', id, 'total');

        // Payments and discounts from customeracchistory
        const totalPaid = await getSumForCustomer('customeracchistory', id, 'paid');
        const totalDiscount = await getSumForCustomer('customeracchistory', id, 'discount');

        const remaining = parseFloat(totalOrders) - parseFloat(totalDiscount) - parseFloat(totalPaid || 0);

        return {
          id,
          data,
          totalOrders: parseFloat(totalOrders || 0),
          totalPaid: parseFloat(totalPaid || 0),
          totalDiscount: parseFloat(totalDiscount || 0),
          remaining: parseFloat(parseFloat(remaining || 0).toFixed(2))
        };
      }));

      setCustomers(customersResult);

    } catch (err) {
      console.error(err);
      Toast.fire({ icon: 'error', title: err.message });
    } finally {
      setLoading(false);
    }
  };

  const openPaymentModal = (cust) => {
    setModalCustId(cust.id);
    setModalTotal(cust.totalOrders || 0);
    setModalPrevPaid(cust.totalPaid || 0);
    setModalCurrentPaid(0);
    setModalRemaining(cust.remaining || 0);
    setPayType('Cash');
    setPaidDate(new Date().toISOString().split('T')[0]);
    window.$('#text-modal1').modal('show');
  };

  const handleCurrentPaidChange = (val) => {
    const n = parseFloat(val || 0);
    setModalCurrentPaid(val);
    const rem = parseFloat((modalRemaining - n).toFixed(2));
    if (rem < 0) {
      Toast.fire({ icon: 'error', title: 'Remaining should not be less than 0' });
      setModalCurrentPaid(0);
      setModalRemaining(modalRemaining);
      return;
    }
    setModalRemaining(rem);
  };

  const addPayment = async () => {
    if (!modalCustId) return;
    if (parseFloat(modalCurrentPaid || 0) <= 0) return;

    try {
      await addDoc(collection(db, 'customeracchistory'), {
        custid: modalCustId,
        purchaseNo: '',
        purchasedocid: '',
        paiddate: paidDate,
        totalamount: modalTotal || 0,
        paid: parseFloat(modalCurrentPaid || 0),
        remaining: modalRemaining,
        paytype: payType,
        status: 'Active',
        note: 'Paid from customer',
        uid: userData?.uid || null,
        discount: 0,
        created: Timestamp.now()
      });

      Toast.fire({ icon: 'success', title: 'Payment added' });
      window.$('#text-modal1').modal('hide');
      fetchAllCustomersWithAggregates();
    } catch (err) {
      console.error(err);
      Toast.fire({ icon: 'error', title: err.message });
    }
  };

  const openHistoryModal = async (cust) => {
    setModalCustId(cust.id);
    setPayhistory([]);
    try {
      const q = query(collection(db, 'customeracchistory'), where('custid', '==', cust.id), orderBy('paiddate', 'desc'));
      const snap = await getDocs(q);
      const arr = snap.docs.map(d => ({ id: d.id, data: d.data() }));
      setPayhistory(arr);
      window.$('#text-modal2').modal('show');
    } catch (err) {
      console.error(err);
    }
  };

  const deletePayment = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await deleteDoc(doc(db, 'customeracchistory', id));
      Toast.fire({ icon: 'success', title: 'Payment deleted' });
      if (modalCustId) openHistoryModal({ id: modalCustId });
      fetchAllCustomersWithAggregates();
    } catch (err) {
      console.error(err);
      Toast.fire({ icon: 'error', title: err.message });
    }
  };

  const openAddOrderModal = (cust) => {
    setOrderCustId(cust.id);
    setOrderAmount(0);
    setOrderDate(new Date().toISOString().split('T')[0]);
    setOrderDescription('');
    window.$('#text-modal3').modal('show');
  };

  const addOrder = async () => {
    if (!orderCustId) return;
    if (parseFloat(orderAmount || 0) <= 0) return;

    try {
      await addDoc(collection(db, 'orderitems'), {
        custid: orderCustId,
        orderdate: orderDate,
        itemname: orderDescription || 'Debit Entry',
        quantity: 1,
        rate: parseFloat(orderAmount),
        total: parseFloat(orderAmount),
        uid: userData?.uid || null,
        created: Timestamp.now()
      });

      Toast.fire({ icon: 'success', title: 'Order added' });
      window.$('#text-modal3').modal('hide');
      fetchAllCustomersWithAggregates();
    } catch (err) {
      console.error(err);
      Toast.fire({ icon: 'error', title: err.message });
    }
  };

  const openOrdersModal = async (cust) => {
    setOrders([]);
    try {
      const q = query(collection(db, 'orderitems'), where('custid', '==', cust.id), orderBy('orderdate', 'desc'));
      const snap = await getDocs(q);
      const arr = snap.docs.map(d => ({ id: d.id, data: d.data() }));
      setOrders(arr);
      window.$('#text-modal4').modal('show');
    } catch (err) {
      console.error(err);
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await deleteDoc(doc(db, 'orderitems', id));
      Toast.fire({ icon: 'success', title: 'Order deleted' });
      fetchAllCustomersWithAggregates();
      if (orderCustId) openOrdersModal({ id: orderCustId });
    } catch (err) {
      console.error(err);
      Toast.fire({ icon: 'error', title: err.message });
    }
  };

  const filteredCustomers = searchText.trim() === ''
    ? customers
    : customers.filter(c => (c.data.name || '').toLowerCase().includes(searchText.toLowerCase()));

  return (
    <div>
      <div className="content-wrapper">
        <br />
        <section className="content">
          <div className="container-fluid">
            <button className="btn btn-secondary " style={{ float: "right" }} >|| Customer Account ||</button><br /><br />
            <div className="card card-primary card-outline ex1">
              <div className=" p-1">
                &nbsp;&nbsp;<input type="search" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                  className='form-control col-sm-4' placeholder='Type here......' />&nbsp;
              </div>

              <div className="card-body table-responsive p-0" >
                <table className="table table-bordered table-hover table-striped table-head-fixed">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Customer Name</th>
                      <th>Mobile</th>
                      <th>Address</th>
                      <th>Total Amount</th>
                      <th>Paid </th>
                      <th>Remaining</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={8}>Loading...</td></tr>
                    )}

                    {!loading && filteredCustomers.length === 0 && (
                      <tr><td colSpan={8}>No customers found</td></tr>
                    )}

                    {!loading && filteredCustomers.map((item, idx) => (
                      <tr key={item.id}>
                        <td width="80">{idx + 1}</td>
                        <td>{item.data.name}</td>
                        <td>{item.data.mobile}</td>
                        <td>{item.data.address}</td>
                        <td>{item.totalOrders}</td>
                        <td>{item.totalPaid}</td>
                        <td>{item.remaining}</td>
                        <td>
                          <button className="btn btn-sm btn-info" onClick={() => openPaymentModal(item)}><i className="fa fa-reply"></i></button>&nbsp;
                          <button className="btn btn-sm btn-success" onClick={() => openHistoryModal(item)}><i className="fa fa-eye"></i></button>&nbsp;
                          <button className="btn btn-sm btn-warning" onClick={() => openAddOrderModal(item)}><i className="fa fa-plus"></i></button>&nbsp;
                          <button className="btn btn-sm btn-primary" onClick={() => openOrdersModal(item)}><i className="fa fa-forward"></i></button>&nbsp;
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Payment Modal */}
      <div className="modal fade" id="text-modal1">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Pay Customer Amount</h4>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">×</span></button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-4">
                  <label> Total Amount</label>
                  <input type="text" className='form-control' style={{ height: 35, fontSize: 14 }} readOnly value={modalTotal} />
                </div>
                <div className="col-sm-4">
                  <label>Previous Paid</label>
                  <input type="text" className='form-control' style={{ height: 35, fontSize: 14 }} value={modalPrevPaid} readOnly />
                </div>
                <div className="col-sm-4">
                  <label>Current Paid</label>
                  <input type="number" className='form-control' style={{ height: 35, fontSize: 14 }} value={modalCurrentPaid} onChange={(e) => handleCurrentPaidChange(e.target.value)} placeholder='Enter Paid Amount' />
                </div>
                <div className="col-sm-4 mt-2">
                  <label>Remaining</label>
                  <input type="text" className='form-control' style={{ height: 35, fontSize: 14 }} value={modalRemaining} readOnly />
                </div>
                <div className="col-sm-4 mt-2">
                  <label>Pay Type</label>
                  <select className='form-control' style={{ height: 35, fontSize: 14 }} value={payType} onChange={(e) => setPayType(e.target.value)}>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                    <option value="RTGS">RTGS</option>
                    <option value="Online Pay">Online Pay</option>
                  </select>
                </div>
                <div className="col-sm-4 mt-2">
                  <label>Paid Date</label>
                  <input type="date" className="form-control" style={{ height: 35, fontSize: 14 }} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
                </div>
              </div><br />
            </div>
            <div className="modal-footer justify-content-between">
              <button type="button" onClick={() => addPayment()} className="btn btn-primary">Add Payment </button>
              <button type="button" className="btn btn-danger" data-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History Modal */}
      <div className="modal fade" id="text-modal2">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Customer Payment History</h4>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">×</span></button>
            </div>
            <div className="modal-body" ref={printRef}>
              <table className='customers' style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Paid Amount</th>
                    <th>Remaining</th>
                    <th>Pay Type</th>
                    <th>Note</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {payhistory.map((p, i) => (
                    <tr key={p.id}>
                      <td>{moment(p.data.paiddate).format('DD-MM-YYYY')}</td>
                      <td>{p.data.paid}</td>
                      <td>{p.data.remaining}</td>
                      <td>{p.data.paytype}</td>
                      <td>{p.data.note}</td>
                      <td><button className='btn btn-sm btn-danger' onClick={() => deletePayment(p.id)}><i className='fa fa-trash'></i></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer justify-content-between">
              <button type="button" className="btn btn-info" onClick={handlePrint}>Print</button>
              <button type="button" className="btn btn-danger" data-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Order Modal */}
      <div className="modal fade" id="text-modal3">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Customer Amount (Debit)</h4>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">×</span></button>
            </div>
            <div className="modal-body">
              <div className='row'>
                <div className='col-sm-4'>
                  <label>Amount</label>
                  <input type='number' className='form-control' value={orderAmount} onChange={(e) => setOrderAmount(e.target.value)} />
                </div>
                <div className='col-sm-4'>
                  <label>Date</label>
                  <input type='date' className='form-control' value={orderDate} onChange={(e) => setOrderDate(e.target.value)} />
                </div>
                <div className='col-sm-4'>
                  <label>Description</label>
                  <input type='text' className='form-control' value={orderDescription} onChange={(e) => setOrderDescription(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer justify-content-between">
              <button type='button' className='btn btn-primary' onClick={() => addOrder()}>Add Amount</button>
              <button type='button' className='btn btn-danger' data-dismiss='modal'>Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* Orders List Modal */}
      <div className="modal fade" id="text-modal4">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Customer Debits Entry</h4>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">×</span></button>
            </div>
            <div className="modal-body">
              <table className='customers' style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td>{moment(o.data.orderdate).format('DD-MM-YYYY')}</td>
                      <td>{o.data.total}</td>
                      <td>{o.data.itemname}</td>
                      <td><button className='btn btn-sm btn-danger' onClick={() => deleteOrder(o.id)}><i className='fa fa-trash'></i></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-footer justify-content-between">
              <button type='button' className='btn btn-danger' data-dismiss='modal'>Close</button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
